import React, { useEffect, useRef } from'react';
import { gsap } from'../../animations/gsap.config';
import { useApp } from'../../context/AppContext';

const Loader = () => {
 const { finishLoad } = useApp();

 const loaderRef = useRef(null);
 const logoRef = useRef(null);
 const logoTextRef = useRef(null);
 const subtitleRef = useRef(null);
 const circleRef = useRef(null);
 const dotsRef = useRef([]);
 const overlayRef = useRef(null);

 useEffect(() => {
 // Remove initial HTML loader immediately
 const htmlLoader = document.getElementById('initial-loader');
 if (htmlLoader) htmlLoader.classList.add('hidden');

 const ctx = gsap.context(() => {
 const tl = gsap.timeline({
 onComplete: () => {
 finishLoad();
 },
 });

 /* ── 1. Entry: logo & circle ── */
 tl.fromTo(
 circleRef.current,
 { scale: 0, opacity: 0 },
 { scale: 1, opacity: 1, duration: 0.8, ease:'back.out(1.7)' },
 )
 .fromTo(
 logoRef.current,
 { y: 30, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.6, ease:'power3.out' },
'-=0.4',
 )
 .fromTo(
 logoTextRef.current,
 { y: 20, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.5, ease:'power3.out' },
'-=0.3',
 )
 .fromTo(
 subtitleRef.current,
 { y: 15, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.5, ease:'power3.out' },
'-=0.2',
 );

 /* ── 2. Progress bar removed ── */

 /* ── 3. Dots pulse ── */
 tl.fromTo(
 dotsRef.current,
 { scale: 0, opacity: 0 },
 {
 scale: 1,
 opacity: 1,
 stagger: 0.12,
 duration: 0.4,
 ease:'back.out(2)',
 },
'-=1.8',
 );

 /* ── 4. Continuous dot pulse loop ── */
 gsap.to(dotsRef.current, {
 scale: 1.4,
 opacity: 0.5,
 stagger: { each: 0.2, repeat: -1, yoyo: true },
 duration: 0.5,
 ease:'sine.inOut',
 });

 /* ── 5. Exit: slide panels out ── */
 tl.to(
 [logoRef.current, logoTextRef.current, subtitleRef.current],
 { y: -30, opacity: 0, stagger: 0.08, duration: 0.5, ease:'power3.in' },
 )
 .to(
 circleRef.current,
 { scale: 0, opacity: 0, duration: 0.4, ease:'power3.in' },
'<',
 )
 .to(
 overlayRef.current,
 {
 yPercent: -100,
 duration: 1,
 ease:'power4.inOut',
 },
'-=0.1',
 )
 .to(
 loaderRef.current,
 { opacity: 0, pointerEvents:'none', duration: 0.3 },
 );

 /* ── 6. Remove circle rotation ── */
 // Circle rotation removed - logo stays static
 }, loaderRef);

 return () => ctx.revert();
 }, [finishLoad]);

 return (
 <div
 ref={loaderRef}
 className="fixed inset-0 z-[99999] flex flex-col items-center justify-center overflow-hidden"
 aria-label="Loading Agora Consulting"
 role="status"
 >
 {/* White overlay that slides away */}
 <div
 ref={overlayRef}
 className="absolute inset-0 bg-white"
 style={{
 background:
'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #eff6ff 100%)',
 }}
 />

 {/* Grid pattern */}
 <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

 {/* Content */}
 <div className="relative z-10 flex flex-col items-center gap-8">

 {/* Rotating circle decoration */}
 <div ref={circleRef} className="relative w-32 h-32">
 {/* Outer dashed circle */}
 <svg
 className="absolute inset-0 w-full h-full"
 viewBox="0 0 128 128"
 fill="none"
 >
 <circle
 cx="64" cy="64" r="60"
 stroke="rgba(29,78,216,0.3)"
 strokeWidth="1"
 strokeDasharray="6 4"
 />
 <circle
 cx="64" cy="64" r="48"
 stroke="rgba(245,158,11,0.4)"
 strokeWidth="1.5"
 strokeDasharray="3 6"
 />
 </svg>

 {/* Center logo mark */}
 <div className="absolute inset-0 flex items-center justify-center">
 <div className="w-32 h-32 rounded-2xl overflow-hidden flex items-center justify-center shadow-glow-primary">
 <img
 src="/images/agora.png"
 alt="Agora Consulting Logo"
 className="w-full h-full object-contain"
 />
 </div>
 </div>

 {/* Orbiting dot */}
 <div
 className="absolute w-3 h-3 rounded-full bg-secondary-400 shadow-glow-secondary"
 style={{
 top:'2px',
 left:'50%',
 transform:'translateX(-50%)',
 }}
 />
 </div>

 {/* Logo text */}
 <div className="flex flex-col items-center gap-2">
 <div ref={logoRef} className="flex items-center gap-3">
 <span className="font-heading font-black text-4xl sm:text-5xl tracking-widest text-slate-900 uppercase">
 Agora
 </span>
 <div className="w-px h-10 bg-secondary-400/60" />
 <span className="font-heading font-light text-4xl sm:text-5xl tracking-widest text-secondary-400 uppercase">
 Consulting
 </span>
 </div>
 <div
 ref={logoTextRef}
 className="h-px w-56 bg-gradient-to-r from-transparent via-primary-400 to-transparent"
 />
 <p
 ref={subtitleRef}
 className="text-slate-600 text-sm tracking-[0.3em] uppercase font-light"
 >
 Research · Strategy · Impact
 </p>
 </div>

 {/* Loading dots */}
 <div className="flex items-center gap-3">
 {[0, 1, 2, 3, 4].map((i) => (
 <div
 key={i}
 ref={(el) => (dotsRef.current[i] = el)}
 className="rounded-full"
 style={{
 width: i === 2 ?'10px' :'6px',
 height: i === 2 ?'10px' :'6px',
 background: i === 2
 ?'var(--color-secondary)'
 :'rgba(29,78,216,0.6)',
 }}
 />
 ))}
 </div>

 {/* Bottom text */}
 <div className="absolute bottom-8 left-0 right-0 flex justify-center">
 <p className="text-xs text-slate-600 tracking-[0.4em] uppercase">
 Addis Ababa, Ethiopia
 </p>
 </div>
 </div>
 </div>
 );
};

export default Loader;